import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  Signal,
  computed,
  signal,
} from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NgMaterialModule } from '@principle-theorem/ng-material';
import { NgSharedModule } from '@principle-theorem/ng-shared';
import {
  Automation,
  AutomationConfiguration,
  TimezoneResolver,
} from '@principle-theorem/principle-core';
import {
  AnyAutomation,
  IAutomation,
  IAutomationConfiguration,
  IAutomationResource,
  isAutomatedFormIssueConfiguration,
  isAutomatedNotificationConfiguration,
  isEventable,
  isFormIssueAutomation,
  isGeneratedTaskConfiguration,
  isNotificationAutomation,
  isTaskAutomation,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  Timestamp,
  WithRef,
  bufferedQuery$,
  multiConcatMap,
  toTimestamp,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface IUpdateExistingAutomationsRequest {
  config: WithRef<IAutomationConfiguration>;
  automationCount: number;
}

@Component({
  selector: 'pr-update-existing-automations-dialog',
  templateUrl: './update-existing-automations-dialog.component.html',
  styleUrls: ['./update-existing-automations-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, NgMaterialModule, NgSharedModule],
})
export class UpdateExistingAutomationsDialogComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();

  loading = signal(true);
  queryTotal = signal(0);

  updating = signal(false);
  current = signal(0);
  progress: Signal<number>;

  complete = signal(false);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IUpdateExistingAutomationsRequest
  ) {
    this.queryTotal.set(data.automationCount);

    this.progress = computed(() => {
      const queryTotal = this.queryTotal();
      if (queryTotal === undefined) {
        return 0;
      }
      return (this.current() / queryTotal) * 100;
    });
  }

  async updateAutomations(): Promise<void> {
    this.updating.set(true);
    await bufferedQuery$(
      AutomationConfiguration.automationsQuery(
        this.data.config.ref,
        toTimestamp()
      ),
      10,
      'ref'
    )
      .pipe(
        multiConcatMap(async (automation) => {
          await this._updateAutomation(automation);
          this.current.update((prev) => prev + 1);
        }),
        takeUntil(this._onDestroy$)
      )
      .toPromise();
    this.updating.set(false);
    this.complete.set(true);
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  private async _updateAutomation(
    automation: WithRef<IAutomation<AnyAutomation>>
  ): Promise<void> {
    if (Automation.hasAlreadyRun(automation)) {
      return;
    }

    const timezone = await TimezoneResolver.fromPracticeRef(
      automation.practiceRef
    );

    if (Automation.isStale(automation, timezone)) {
      return;
    }

    if (
      isTaskAutomation(automation) &&
      isGeneratedTaskConfiguration(this.data.config)
    ) {
      return this._saveAutomationChanges(automation, {
        timing: this.data.config.timing,
        requiredConditions: this.data.config.requiredConditions,
        title: this.data.config.title,
        assignee: this.data.config.assignee,
        comment: this.data.config.comment,
        isForTeam: this.data.config.isForTeam,
        priority: this.data.config.priority,
      });
    }

    if (
      isNotificationAutomation(automation) &&
      isAutomatedNotificationConfiguration(this.data.config)
    ) {
      return this._saveAutomationChanges(automation, {
        timing: this.data.config.timing,
        requiredConditions: this.data.config.requiredConditions,
        name: this.data.config.name,
        subject: this.data.config.subject,
        type: this.data.config.type,
      });
    }

    if (
      isFormIssueAutomation(automation) &&
      isAutomatedFormIssueConfiguration(this.data.config)
    ) {
      return this._saveAutomationChanges(automation, {
        timing: this.data.config.timing,
        name: this.data.config.name,
        requiredConditions: this.data.config.requiredConditions,
        formConfigurationRef: this.data.config.formConfigurationRef,
        specialForm: this.data.config.specialForm,
      });
    }
  }

  private async _saveAutomationChanges<T extends IAutomationResource>(
    automation: WithRef<IAutomation<T>>,
    data: Partial<T>
  ): Promise<void> {
    const triggerDate = await this._getNewTriggerDate({
      ...automation,
      data: { ...automation.data, ...data },
    });

    const updatedAutomation: WithRef<IAutomation<T>> = {
      ...automation,
      triggerDate,
      data: {
        ...automation.data,
        ...data,
      },
    };
    await Firestore.saveDoc(updatedAutomation);
  }

  private async _getNewTriggerDate<T extends IAutomationResource>(
    automation: WithRef<IAutomation<T>>
  ): Promise<Timestamp | undefined> {
    if (!automation.triggerDate) {
      return;
    }

    const appointment = await Automation.resolveAppointment(automation);
    if (!appointment || !isEventable(appointment)) {
      return;
    }

    const timezone = await TimezoneResolver.fromEvent(appointment.event);

    const expectedTriggerDate = Automation.getExpectedTriggerDate(
      automation,
      appointment.event,
      timezone
    );

    const now = moment.tz(timezone);
    const triggerDate = Automation.applyGracePeriod(
      expectedTriggerDate,
      timezone,
      now
    );

    return toTimestamp(triggerDate);
  }
}
